/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, runInAction } from "mobx"
import { Howl } from "howler"
import {
	contentControllerGetManifestForProcessingContent,
	type TaskAudioChunk,
} from "@/shared/api/generated"
import { indexDBService } from "@/entities/indexdb"
import { addAudioBreadcrumb } from "@/entities/sentry"
import type { AudioPlaybackRate } from "./types"

const BLOB_STORAGE_URL = import.meta.env.VITE_BLOB_STORAGE_URL as string

// Fallback only: the manifest carries each chunk's public `url`, so the
// client does not have to know the bucket's domain (it moved twice already).
const fallbackChunkAudioUrl = (taskId: string, chunkIdx: number) =>
	`${BLOB_STORAGE_URL}/tasks/${taskId}/audio_${chunkIdx}.mp3`

interface ChunkState {
	chunk: TaskAudioChunk
	howl: Howl | null
	loaded: boolean
}

/**
 * Chunked audio engine for content with status PARTIALLY_COMPLETED_AND_STILL_PROCESSING.
 *
 * Presents a unified globalPosition / totalDuration to the outside world,
 * while internally loading and switching between individual chunk mp3 files.
 */
class ChunkedAudioEngine {
	// --- public observable state (mirrors AudioEngine API) ---
	contentId: string | null = null
	title: string | null = null
	totalDuration = 0
	globalPosition = 0
	playbackRate: AudioPlaybackRate = 1
	isPlaying = false
	isLoading = false

	// --- manifest data ---
	private taskId: string | null = null
	private chunks: TaskAudioChunk[] = []

	// --- chunk playback ---
	private chunkStates: Map<number, ChunkState> = new Map()
	private activeChunkIdx: number | null = null
	private rafId: number | null = null

	/** True while the chunk being played was delivered audio-first and its
	 * word timestamps are still being generated (manifest wordsReady=false).
	 * Observable; kept in sync by syncActiveChunkWordsPending(). */
	private activeChunkWordsPending = false

	constructor() {
		// waitingForNextChunk used to be excluded from observability entirely,
		// so nothing outside this class could ever react to it - the UI had no
		// way to tell "genuinely stalled waiting for the server" apart from
		// "user pressed pause" (both look like isPlaying=false, isLoading=false).
		// It's now a plain observable field, exposed read-only via the
		// isWaitingForChunk computed getter below.
		makeAutoObservable<
			ChunkedAudioEngine,
			| "taskId"
			| "chunks"
			| "chunkStates"
			| "activeChunkIdx"
			| "rafId"
			| "handleBeforeUnload"
		>(this, {
			taskId: false,
			chunks: false,
			chunkStates: false,
			activeChunkIdx: false,
			rafId: false,
			handleBeforeUnload: false,
		}, { autoBind: true })

		window.addEventListener("beforeunload", this.handleBeforeUnload)
	}

	// ─── Public API ───────────────────────────────────────────

	/** Load manifest and start playing from a global position */
	async start(
		contentId: string,
		title: string,
		options?: { startPosition?: number; playbackRate?: AudioPlaybackRate }
	) {
		this.stop()

		this.contentId = contentId
		this.title = title
		this.isLoading = true
		this.playbackRate = options?.playbackRate ?? this.playbackRate

		addAudioBreadcrumb("Chunked audio start", { contentId, title, startPosition: options?.startPosition })

		try {
			const manifest =
				await contentControllerGetManifestForProcessingContent(contentId)

			runInAction(() => {
				this.taskId = manifest.taskId
				this.chunks = manifest.chunks
				this.totalDuration = manifest.totalAudioTime
			})

			const startPos = options?.startPosition ?? 0
			this.playFromGlobalPosition(startPos)
		} catch (error) {
			console.error("ChunkedAudioEngine: failed to load manifest", error)
			addAudioBreadcrumb("Chunked audio manifest load error", { contentId, error: String(error) })
			runInAction(() => {
				this.isLoading = false
			})
		}
	}

	/** Load manifest and set metadata without playing. Common player shows the item. */
	async load(
		contentId: string,
		title: string,
		startPosition = 0
	) {
		this.stop()

		this.contentId = contentId
		this.title = title
		this.isLoading = true

		addAudioBreadcrumb("Chunked audio load", { contentId, title, startPosition })

		try {
			const manifest =
				await contentControllerGetManifestForProcessingContent(contentId)

			runInAction(() => {
				this.taskId = manifest.taskId
				this.chunks = manifest.chunks
				this.totalDuration = manifest.totalAudioTime
				this.globalPosition = startPosition
				this.isLoading = false
				this.isPlaying = false
			})
		} catch (error) {
			console.error("ChunkedAudioEngine: failed to load manifest", error)
			runInAction(() => {
				this.isLoading = false
			})
		}
	}

	/** Whether playback stopped because there were no more chunks */
	private waitingForNextChunk = false

	/** Public, reactive read of waitingForNextChunk - drive a loader from this. */
	get isWaitingForChunk(): boolean {
		return this.waitingForNextChunk
	}

	/** Reactive: the active chunk plays but its highlighting isn't ready yet. */
	get isHighlightPending(): boolean {
		return this.activeChunkWordsPending
	}

	private syncActiveChunkWordsPending() {
		const chunk =
			this.activeChunkIdx === null
				? undefined
				: this.chunks.find(c => c.chunkIdx === this.activeChunkIdx)
		this.activeChunkWordsPending = chunk ? chunk.wordsReady === false : false
	}

	/** Refresh manifest to pick up newly available chunks */
	async refreshManifest() {
		if (!this.contentId) return
		try {
			const manifest =
				await contentControllerGetManifestForProcessingContent(
					this.contentId
				)
			// Two refreshManifest() calls can be in flight at once (a socket
			// CONTENT_NEW_CHUNK_COMPLETED event and, separately, this class's own
			// retry-after-stall path) and resolve out of order. The chunk list
			// only grows while a note is processing, so a response with fewer
			// chunks than we already have lost the race - applying it would roll
			// the manifest back and could make playback wrongly conclude "no next
			// chunk yet" for a chunk that had already arrived.
			if (manifest.chunks.length < this.chunks.length) return
			runInAction(() => {
				this.taskId = manifest.taskId
				this.chunks = manifest.chunks
				this.totalDuration = manifest.totalAudioTime
				this.syncActiveChunkWordsPending()
			})
		} catch (error) {
			console.error("ChunkedAudioEngine: failed to refresh manifest", error)
		}
	}

	pause() {
		const active = this.getActiveHowl()
		if (active) {
			active.pause()
		}
		this.isPlaying = false
		this.stopRafLoop()
		this.persistPosition()
	}

	resume() {
		const active = this.getActiveHowl()
		if (active) {
			active.play()
			this.isPlaying = true
			this.startRafLoop()
		} else if (this.contentId && this.chunks.length > 0) {
			// After load() — no Howl yet, start playback from saved position
			this.playFromGlobalPosition(this.globalPosition)
		}
	}

	stop() {
		this.persistPosition()
		this.stopRafLoop()
		this.destroyAllChunks()
		this.waitingForNextChunk = false
		this.contentId = null
		this.title = null
		this.taskId = null
		this.chunks = []
		this.totalDuration = 0
		this.globalPosition = 0
		this.isPlaying = false
		this.isLoading = false
		this.activeChunkIdx = null
	}

	/** Seek to a global position (seconds) */
	seekTo(globalPos: number) {
		this.playFromGlobalPosition(globalPos)
	}

	setPlaybackRate(rate: AudioPlaybackRate) {
		this.playbackRate = rate
		for (const state of this.chunkStates.values()) {
			state.howl?.rate(rate)
		}
	}

	// ─── Internals ────────────────────────────────────────────

	private playFromGlobalPosition(globalPos: number) {
		if (!this.taskId || this.chunks.length === 0) return

		// Find which chunk contains this global position
		const { chunkIdx, localPosition } =
			this.resolveGlobalPosition(globalPos)

		if (chunkIdx === null) return

		this.stopRafLoop()

		runInAction(() => {
			this.globalPosition = globalPos
		})

		// Stop ALL playing howls before starting the target chunk
		for (const [, state] of this.chunkStates.entries()) {
			if (state.howl && state.howl.playing()) {
				state.howl.stop()
			}
		}

		this.activeChunkIdx = chunkIdx
		this.syncActiveChunkWordsPending()
		this.loadAndPlayChunk(chunkIdx, localPosition)
		this.prefetchAdjacentChunks(chunkIdx)
		this.evictDistantChunks(chunkIdx)
	}

	private resolveGlobalPosition(globalPos: number): {
		chunkIdx: number | null
		localPosition: number
	} {
		let elapsed = 0
		for (const chunk of this.chunks) {
			if (globalPos < elapsed + chunk.chunkAudioTime) {
				return {
					chunkIdx: chunk.chunkIdx,
					localPosition: globalPos - elapsed,
				}
			}
			elapsed += chunk.chunkAudioTime
		}
		// Past all chunks — clamp to last chunk end
		if (this.chunks.length > 0) {
			const last = this.chunks[this.chunks.length - 1]
			return { chunkIdx: last.chunkIdx, localPosition: last.chunkAudioTime }
		}
		return { chunkIdx: null, localPosition: 0 }
	}

	/** Global start time of a chunk (sum of all previous chunks) */
	private chunkGlobalStart(chunkIdx: number): number {
		let start = 0
		for (const chunk of this.chunks) {
			if (chunk.chunkIdx === chunkIdx) return start
			start += chunk.chunkAudioTime
		}
		return start
	}

	private chunkAudioUrl(chunkIdx: number): string {
		const chunk = this.chunks.find(c => c.chunkIdx === chunkIdx)
		if (chunk?.url) return chunk.url
		return fallbackChunkAudioUrl(this.taskId ?? "", chunkIdx)
	}

	private loadAndPlayChunk(chunkIdx: number, localPosition: number) {
		if (!this.taskId) return
		const url = this.chunkAudioUrl(chunkIdx)

		let state = this.chunkStates.get(chunkIdx)

		if (!state) {
			const howl = this.createHowl(url, chunkIdx)
			state = {
				chunk: this.chunks.find(c => c.chunkIdx === chunkIdx)!,
				howl,
				loaded: false,
			}
			this.chunkStates.set(chunkIdx, state)
		}

		if (state.loaded && state.howl) {
			// Stop first to kill any existing sound IDs, then seek + play
			if (state.howl.playing()) {
				state.howl.stop()
			}
			state.howl.seek(localPosition)
			state.howl.play()
			runInAction(() => {
				this.isLoading = false
				this.isPlaying = true
			})
			this.startRafLoop()
		} else {
			// Will play on load via onload callback
			runInAction(() => {
				this.isLoading = true
			})
				// Store desired start position for onload
				; (state as any)._pendingSeek = localPosition
		}
	}

	private createHowl(url: string, chunkIdx: number): Howl {
		const state = () => this.chunkStates.get(chunkIdx)

		const howl = new Howl({
			src: [url],
			rate: this.playbackRate,
			onload: () => {
				const s = state()
				if (s) s.loaded = true

				// Only auto-play if this is still the active chunk
				if (this.activeChunkIdx !== chunkIdx) return

				// Stop any lingering sound IDs on this howl before playing
				howl.stop()
				const pendingSeek = (s as any)?._pendingSeek ?? 0
				if (pendingSeek > 0) howl.seek(pendingSeek)
				howl.play()
				runInAction(() => {
					this.isLoading = false
					this.isPlaying = true
				})
				this.startRafLoop()
			},
			onend: () => {
				// Move to next chunk
				if (this.activeChunkIdx === chunkIdx) {
					this.advanceToNextChunk()
				}
			},
			onloaderror: (_id, error) => {
				console.error(
					`ChunkedAudioEngine: chunk ${chunkIdx} load error`,
					error
				)
				// This also fires for prefetched neighbor chunks, which aren't
				// what's currently supposed to be playing - only react if the
				// failed chunk is the active one.
				if (this.activeChunkIdx !== chunkIdx) return
				this.retryActiveChunkAfterError()
			},
			onplayerror: (_id, error) => {
				console.error(
					`ChunkedAudioEngine: chunk ${chunkIdx} play error`,
					error
				)
				if (this.activeChunkIdx !== chunkIdx) return
				this.retryActiveChunkAfterError()
			},
		})

		return howl
	}

	private advanceToNextChunk() {
		if (this.activeChunkIdx === null) return

		const currentIdx = this.chunks.findIndex(
			c => c.chunkIdx === this.activeChunkIdx
		)
		const nextChunk = this.chunks[currentIdx + 1]

		if (nextChunk) {
			this.activeChunkIdx = nextChunk.chunkIdx
			this.loadAndPlayChunk(nextChunk.chunkIdx, 0)
			this.prefetchAdjacentChunks(nextChunk.chunkIdx)
			this.evictDistantChunks(nextChunk.chunkIdx)
		} else {
			// No more chunks — pause and wait for a new chunk to arrive
			this.stopRafLoop()
			this.waitingForNextChunk = true
			runInAction(() => {
				this.isPlaying = false
			})
		}
	}

	/**
	 * A chunk can fail to load/play if it's momentarily unavailable - e.g. the
	 * whole task just finished and the server is deleting per-chunk files
	 * right as we try to play one. Without this, the engine used to just
	 * silently stop (isPlaying/isLoading flip false) with no recovery and no
	 * visible indication why. Surface the stall via isWaitingForChunk and
	 * retry once with a fresh manifest - if the note is genuinely done,
	 * CONTENT_CREATION_FINISHED (handled in process-socket store) swaps to
	 * the merged file shortly anyway and this retry becomes moot.
	 */
	private retryActiveChunkAfterError() {
		if (this.activeChunkIdx === null) return
		const chunkIdx = this.activeChunkIdx
		const localPosition = Math.max(
			0,
			this.globalPosition - this.chunkGlobalStart(chunkIdx)
		)

		this.stopRafLoop()
		// Drop the broken Howl instance so the retry creates a fresh one
		// instead of reusing whatever bad state the error left behind.
		this.chunkStates.get(chunkIdx)?.howl?.unload()
		this.chunkStates.delete(chunkIdx)

		runInAction(() => {
			this.isLoading = false
			this.isPlaying = false
			this.waitingForNextChunk = true
		})

		void this.refreshManifest().then(() => {
			if (!this.waitingForNextChunk || this.activeChunkIdx !== chunkIdx) {
				return
			}
			this.waitingForNextChunk = false
			this.loadAndPlayChunk(chunkIdx, localPosition)
		})
	}

	/**
	 * Called when the server notifies that a new chunk is ready.
	 * Refreshes manifest and resumes playback if we were waiting.
	 */
	/** Server says a chunk's word timestamps landed (or failed): re-read the
	 * manifest so wordsReady flags (and the highlight-pending state) update. */
	async onChunkWordsAvailable(contentId: string) {
		if (this.contentId !== contentId) return
		await this.refreshManifest()
	}

	async onNewChunkAvailable(contentId: string) {
		if (this.contentId !== contentId) return

		await this.refreshManifest()

		if (this.waitingForNextChunk) {
			this.waitingForNextChunk = false
			// Resume from where we left off
			this.playFromGlobalPosition(this.globalPosition)
		}
	}

	private prefetchAdjacentChunks(chunkIdx: number) {
		if (!this.taskId) return
		const currentArrayIdx = this.chunks.findIndex(
			c => c.chunkIdx === chunkIdx
		)

		for (const offset of [-1, 1]) {
			const neighbor = this.chunks[currentArrayIdx + offset]
			if (!neighbor) continue
			if (this.chunkStates.has(neighbor.chunkIdx)) continue

			const url = neighbor.url || this.chunkAudioUrl(neighbor.chunkIdx)
			const howl = this.createHowl(url, neighbor.chunkIdx)
			this.chunkStates.set(neighbor.chunkIdx, {
				chunk: neighbor,
				howl,
				loaded: false,
			})
		}
	}

	/** Keep only active ± 1 chunks in memory */
	private evictDistantChunks(activeChunkIdx: number) {
		const activeArrayIdx = this.chunks.findIndex(
			c => c.chunkIdx === activeChunkIdx
		)
		const keepSet = new Set<number>()

		for (const offset of [-1, 0, 1]) {
			const c = this.chunks[activeArrayIdx + offset]
			if (c) keepSet.add(c.chunkIdx)
		}

		for (const [idx, state] of this.chunkStates.entries()) {
			if (!keepSet.has(idx)) {
				state.howl?.unload()
				this.chunkStates.delete(idx)
			}
		}
	}

	private getActiveHowl(): Howl | null {
		if (this.activeChunkIdx === null) return null
		return this.chunkStates.get(this.activeChunkIdx)?.howl ?? null
	}

	// ─── RAF position tracking ────────────────────────────────

	private startRafLoop() {
		this.stopRafLoop()
		const tick = () => {
			const howl = this.getActiveHowl()

			// Howl.play() is async — keep polling until it actually starts
			if (!howl || !howl.playing()) {
				if (this.isPlaying) {
					this.rafId = requestAnimationFrame(tick)
				}
				return
			}

			const localPos = howl.seek() as number
			const globalStart = this.chunkGlobalStart(this.activeChunkIdx!)

			runInAction(() => {
				this.globalPosition = globalStart + localPos
			})

			this.rafId = requestAnimationFrame(tick)
		}
		this.rafId = requestAnimationFrame(tick)
	}

	private stopRafLoop() {
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId)
			this.rafId = null
		}
	}

	private handleBeforeUnload = () => {
		this.persistPosition()
	}

	private persistPosition() {
		if (this.contentId && this.globalPosition > 0) {
			indexDBService.saveAudioPosition({
				id: this.contentId,
				position: this.globalPosition,
			})
		}
	}

	private destroyAllChunks() {
		for (const state of this.chunkStates.values()) {
			state.howl?.unload()
		}
		this.chunkStates.clear()
	}
}

export const chunkedAudioEngine = new ChunkedAudioEngine()
